import React, {FC, memo, useEffect, useState} from "react";
import {isBrowser} from "@montana/helpers/browser.helper";
import {
    IM031BaseModelCard,
    IM031BaseModelCarousel,
    IM031BaseModelCarouselTab
} from "@montana/models/generated/cms.interface";
import {ProductCardProfessional} from "@montana/shared/product-card-professional";
import {Link} from "@montana/shared/link";
import {RichText} from "@montana/shared/rich-text";
import {Button, ButtonStyles} from "@montana/shared/button";
import {Responsive} from "@montana/shared/responsive";
import {Dropdown} from "@montana/shared/dropdown";
import {IFieldOptionValue} from "@montana/shared/form/field/field";
import {useTranslations} from "@montana/containers/translations";
import Carousel from "@montana/shared/carousel";

export const M031BaseModelCarouselBlock: FC<IM031BaseModelCarousel> = memo(({ header, carouselTabs, viewAllLink }: IM031BaseModelCarousel) => {
    if (!isBrowser) return <></>;

    const [activeTab, setActiveTab] = useState<IM031BaseModelCarouselTab | undefined>((carouselTabs?.length === 1) ? carouselTabs[0] : undefined);
    const [shownBaseModels, setShownBaseModels] = useState<IM031BaseModelCard[]>([]);
    const [tabOptions, setTabOptions] = useState<IFieldOptionValue[]>([]);

    const translate = useTranslations();

    useEffect(() => {
        if (!activeTab) {
            showAll();
        } else {
            setShownBaseModels(activeTab?.baseModels || []);
        }
    }, [activeTab])

    useEffect(() => {
        let carouselTabOptions: IFieldOptionValue[] = [];
        if (!!carouselTabs && carouselTabs.length > 1) {
            carouselTabOptions = [{ value: '-1', text: translate(t => t.general.all, '[All]') }];
        }
        carouselTabOptions = [...carouselTabOptions, ...(carouselTabs?.map((tab, i) => ({ value: `${i}`, text: tab.title })) || [])];
        setTabOptions(carouselTabOptions);
    }, [carouselTabs])

    const showAll = () => {
        setShownBaseModels(carouselTabs?.map(tab => tab?.baseModels || [])
            .reduce((acc, baseModels) => [...acc, ...baseModels], []) || []);
    }

    return (
        <section className="base-model-carousel">
            {header && <RichText className="base-model-carousel__header" text={header} disableAppear />}
            {shownBaseModels && (
                <Carousel
                    header={(
                        <div className="base-model-carousel__navigation">
                            <Responsive maxWidth={600}>
                                {tabOptions?.length > 1 ? (
                                    <Dropdown
                                        className="base-model-carousel__tab-dropdown"
                                        options={tabOptions}
                                        value={activeTab ? `${carouselTabs?.indexOf(activeTab)}` : '-1'}
                                        onChange={(_: string, __: string, index: number) => setActiveTab(() => carouselTabs && index > 0 ? carouselTabs[index - 1] : undefined)}
                                    />
                                ) : <div></div>}
                            </Responsive>
                            <Responsive minWidth={601}>
                                <div className="base-model-carousel__navigation-tabs">
                                    {!!carouselTabs && carouselTabs.length > 1 && <Button inverseStrokeAnimation strokeIsActive={!activeTab} style={ButtonStyles.Text} onClick={() => setActiveTab(undefined)} text={translate(t => t.general.all, '[All]')} />}
                                    {carouselTabs?.filter(tab => !!tab.baseModels?.length)
                                        .map((tab, index) => <Button key={index} inverseStrokeAnimation strokeIsActive={activeTab?.title === tab.title} style={ButtonStyles.Text} onClick={() => setActiveTab(tab)} text={tab.title || ''} />)
                                    }
                                </div>
                            </Responsive>
                            {!!viewAllLink && (
                                <Link
                                    to={viewAllLink?.url}
                                    title={viewAllLink?.title}
                                    withStroke
                                >
                                    {viewAllLink?.name}
                                </Link>
                            )}
                        </div>
                    )}
                    lowHeaderSpacing
                    insideBrokenContainer
                >
                    {shownBaseModels.map((baseModel, index) => (
                        <ProductCardProfessional
                            key={`${baseModel.name}-${index}`}
                            className="base-model-carousel__card"
                            description={baseModel.description}
                            download3DFile={baseModel.fileLink3DModel}
                            downloadSheet={baseModel.productSheetLink}
                            fromPrice={baseModel.fromPrice}
                            imageUrl={baseModel.productImageViewModel?.url}
                            moreInformation={baseModel.productUrl}
                            title={baseModel.name}
                            gtmProduct={{
                                id: '',
                                name: baseModel.name || '',
                                list: '',
                                position: index + 1,
                                price: baseModel.fromPrice?.amount || 0,
                            }}
                        />
                    ))}
                </Carousel>
            )}
        </section >
    );
});
